export const waterEfficiencyAlgorithm = async (ctx) => {
    const {
        gardenMatrix,
        plants,
        config,
        utils: { validatePlacement, placePlant, createPlantMap, fillRemainingOpenAreas }
    } = ctx;

    if (!Array.isArray(gardenMatrix) || !Array.isArray(gardenMatrix[0])) {
        throw new Error('Expected gardenMatrix to be a 2D array.');
    }

    const rows = gardenMatrix.length;
    const cols = gardenMatrix[0].length;

    let plantMap = createPlantMap(rows, cols);
    let plantsPlaced = 0;

    const plantCounts = {};
    plants.forEach((plant, index) => {
        plantCounts[`${plant.id}_${index}`] = plant.count ?? Infinity;
    });

    // Drought tolerance scoring
    const droughtScore = { high: 0, moderate: 1, low: 2 };

    // Sort based on water efficiency (lowest weekly usage + high drought tolerance)
    const sortedPlants = [...plants].map((plant, index) => {
        // Map database properties to expected properties
        const mappedPlant = {
            ...plant,
            name: plant.plant_name || plant.name,
            image: plant.plant_image_url || plant.image,
            veggieImage: plant.plant_image_url || plant.veggieImage,
            plantingWidth: plant.planting_width || plant.plantingWidth || 1,
            plantingHeight: plant.planting_height || plant.plantingHeight || 1,
            waterFrequency: plant.water_frequency || plant.waterFrequency || 7,
            waterPerApplication: plant.water_per_application || plant.waterPerApplication || 1,
            droughtTolerance: plant.drought_tolerance || plant.droughtTolerance || 'moderate'
        };

        const totalWaterPerWeek = mappedPlant.waterFrequency * mappedPlant.waterPerApplication;
        return {
            ...mappedPlant,
            uniqueKey: `${plant.id}_${index}`,
            waterScore: totalWaterPerWeek + (droughtScore[mappedPlant.droughtTolerance] ?? 1) * 10
        };
    }).sort((a, b) => a.waterScore - b.waterScore); // lower = better

    // Try to place plants
    for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
        for (let colIndex = 0; colIndex < cols; colIndex++) {
            for (const plant of sortedPlants) {
                if (plantCounts[plant.uniqueKey] <= 0) continue;

                if (validatePlacement(plant, plantMap, gardenMatrix, colIndex, rowIndex)) {
                    plantMap = placePlant(plantMap, plant, rowIndex, colIndex);
                    plantCounts[plant.uniqueKey]--;
                    plantsPlaced++;
                    break;
                }
            }
        }
    }

    // Unused plants
    const unusedPlants = sortedPlants
        .filter(p => plantCounts[p.uniqueKey] > 0)
        .map(p => ({
            name: p.name, // Use mapped property
            id: p.id,
            remaining: plantCounts[p.uniqueKey],
            plantingWidth: p.plantingWidth,
            plantingHeight: p.plantingHeight
        }));

    // if autofill remaining empty plots is checked by user fill with filler plants
    let filledMap = plantMap;
    if (config.autoFillEmpty) {
        filledMap = await fillRemainingOpenAreas(gardenMatrix, plantMap, config, plants);
    }

    return {
        plantMap: filledMap,
        plantsPlaced,
        unusedPlants,
        warnings: [],
        efficiency: plantsPlaced / (rows * cols)
    };
};